import {
    collection,
    doc,
    addDoc,
    setDoc,
    deleteDoc,
    getDocs,
    onSnapshot,
    Timestamp,
} from 'firebase/firestore';
import { db } from '../firebaseApp';
import { PROMO_KIND_CODE, PROMO_KIND_DATE } from './promoTypes';

const PROMOS_COLLECTION = 'promos';

/**
 * Convert a Firestore promo doc into the plain shape the rest of the app
 * uses. Timestamps become millis (number) so they survive serialization
 * through Redux without warnings about non-serializable values.
 */
function normaliseDoc(snap) {
    const data = snap.data() || {};
    return {
        id: snap.id,
        kind: data.kind,
        name: data.name || '',
        code: data.code || null,
        discountPercent: Number(data.discountPercent) || 0,
        startsAt: data.startsAt ? data.startsAt.toMillis() : null,
        endsAt: data.endsAt ? data.endsAt.toMillis() : null,
        active: data.active !== false,
    };
}

/**
 * One-shot fetch of all promos. Used at app boot so the resolver has
 * everything it needs without forcing a live listener on every page.
 */
export async function fetchAllPromos() {
    const snap = await getDocs(collection(db, PROMOS_COLLECTION));
    return snap.docs.map(normaliseDoc);
}

/**
 * Live subscription, used only inside the admin UI so edits show up
 * immediately. Returns the unsubscribe function.
 */
export function subscribeToPromos(callback) {
    return onSnapshot(collection(db, PROMOS_COLLECTION), (snap) => {
        callback(snap.docs.map(normaliseDoc));
    });
}

/**
 * Create or update a promo. `data` is the plain shape returned by
 * normaliseDoc (millis-based timestamps). We convert to Firestore
 * Timestamps on write.
 */
export async function upsertPromo(data) {
    const payload = {
        kind: data.kind,
        name: data.name || '',
        code: data.kind === PROMO_KIND_CODE && data.code
            ? data.code.trim().toUpperCase()
            : null,
        discountPercent: Number(data.discountPercent) || 0,
        startsAt: data.startsAt ? Timestamp.fromMillis(data.startsAt) : null,
        endsAt: data.endsAt ? Timestamp.fromMillis(data.endsAt) : null,
        active: data.active !== false,
    };

    if (data.id) {
        await setDoc(doc(db, PROMOS_COLLECTION, data.id), payload);
        return data.id;
    }
    const ref = await addDoc(collection(db, PROMOS_COLLECTION), payload);
    return ref.id;
}

export async function deletePromo(id) {
    await deleteDoc(doc(db, PROMOS_COLLECTION, id));
}

export const PROMO_KINDS = [PROMO_KIND_CODE, PROMO_KIND_DATE];
